import { MyWindow } from 'react-app-env';
import apiService from 'library/api-service';
import { useNavigate } from 'react-router-dom';
import { ProductReportItem } from 'library/types';
import { Alert, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { EXPORT_TYPE_ENUM, STORAGE_KEY_ENUM } from 'enums';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { Authentication, renderDataGridLocaleText } from 'utils';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import environmentTypesActions from 'store/features/environment-types/actions';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { DataGridBox, CustomToolbar, ZebraDataGrid, CustomNoRowsOverlay } from 'components';
import { selectEnvironmentTypes, selectEnvironmentTypesIsEmpty } from 'store/features/environment-types/selector';

declare const window: MyWindow;

const ProductReport = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();

  // redux hookss
  const dispatch = useAppDispatch();

  const permissionCodes = useAppSelector(selectPermissionCodes);

  const products = useAppSelector(selectProjects);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  const environmentTypes = useAppSelector(selectEnvironmentTypes);
  const isEmptyEnvironmentTypes = useAppSelector(selectEnvironmentTypesIsEmpty);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<ProductReportItem[]>([]);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // dynamic variables
  const incompleteAdaptation = isEmptyProducts || isEmptyEnvironmentTypes;

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'product',
        flex: 1,
        headerName: intl.formatMessage({ id: 'report.companyName', defaultMessage: 'Proje Adı' }),
      },
      {
        field: 'projectId',
        flex: 1,
        headerName: intl.formatMessage({ id: 'project.table.product', defaultMessage: 'Ürün' }),
        headerAlign: 'center',
        valueGetter: (value: number | null) => {
          const product = products.find((product) => value === product.id);

          return product?.title ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => products.map((product) => product.title),
      },
      {
        field: 'environmentId',
        flex: 0.5,
        headerName: intl.formatMessage({ id: 'project.table.environmentType', defaultMessage: 'Sunucu Tipi' }),
        headerAlign: 'center',
        valueGetter: (value: number | null) => {
          const environmentType = environmentTypes.find((environmentType) => value === environmentType.id);

          return environmentType?.title ?? '';
        },
        type: 'singleSelect',
        valueOptions: () => environmentTypes.map((environmentType) => environmentType.title),
      },
      {
        field: 'companyCount',
        flex: 0.25,
        headerName: intl.formatMessage({ id: 'productReport.table.companyCount', defaultMessage: 'Şirket Sayısı ' }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
      },
      {
        field: 'usedBankCount',
        flex: 0.25,
        headerName: intl.formatMessage({ id: 'productReport.table.usedBankCount', defaultMessage: 'Banka Sayısı ' }),
        headerAlign: 'center',
        align: 'center',
        type: 'number',
      },
    ],
    [intl, products, environmentTypes]
  );

  // async helper functions
  const getValues = async () => {
    try {
      setLoading(true);

      const response = await apiService.getProductsReport();
      const responseWithId = response.data.map((el, idx) => ({ id: idx + 1, ...el }));

      if (response.isSuccess) {
        setData(responseWithId);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const onClickDownloadHandler = async (exportType: number) => {
    const token = Authentication.getToken();
    const restUrl = process.env.REACT_APP_REST_URL || window.config.REACT_APP_REST_URL;

    const response = await fetch(`${restUrl}/Report/Products/Export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        exportType,
      }),
    });

    const arrayBuffer = await response.arrayBuffer();

    let mimeType = '';
    let fileName = '';

    if (exportType === EXPORT_TYPE_ENUM.EXCEL) {
      mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      fileName = 'export.xlsx';
    } else if (exportType === EXPORT_TYPE_ENUM.CSV) {
      mimeType = 'text/csv;charset=utf-8;';
      fileName = 'export.csv';
    } else {
      mimeType = 'application/octet-stream';
      fileName = 'export.bin';
    }

    const blob = new Blob([arrayBuffer], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();

    URL.revokeObjectURL(url);
  };

  // useEffect hooks
  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('report-page-view')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    if (!environmentTypes.length && !isEmptyEnvironmentTypes) {
      dispatch(environmentTypesActions.getEnvironmentTypes());
    }
  }, [environmentTypes, isEmptyEnvironmentTypes, dispatch]);

  useEffect(() => {
    getValues();
  }, []);

  return incompleteAdaptation ? (
    <Alert severity="info" icon={false}>
      <Typography>
        <FormattedMessage
          id="general.completeAdaptationsText"
          defaultMessage="Sayfayı görüntülemek için lütfen uyarlamaları tamamlayınız..! (Ürün Grupları, Ürünler, Bağlantı Türleri, GİB Yöntemleri, Kullanılan Bankalar)"
        />
      </Typography>
    </Alert>
  ) : (
    <DataGridBox>
      <ZebraDataGrid
        /* Pagination Settings */
        pagination
        /* Chekbox Settings */
        checkboxSelection
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        rowSelectionModel={rowSelectionModel}
        /* Remain Settings */
        columns={columns}
        disableRowSelectionOnClick
        getRowId={(row) => row.id}
        loading={loading}
        localeText={renderDataGridLocaleText()}
        rows={data}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          toolbar: () =>
            CustomToolbar({
              multiExport: true,
              exportAsExcelHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.EXCEL),
              exportAsCsvHandler: () => onClickDownloadHandler(EXPORT_TYPE_ENUM.CSV),
            }),
        }}
        slotProps={{
          filterPanel: {
            filterFormProps: {
              logicOperatorInputProps: {
                variant: 'outlined',
                size: 'small',
              },
              columnInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              operatorInputProps: {
                variant: 'outlined',
                size: 'small',
                sx: { mt: 'auto' },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: 'outlined',
                  size: 'small',
                },
              },
            },
          },
        }}
        disableAggregation
        disableRowGrouping
      />
    </DataGridBox>
  );
};

export default ProductReport;
